import { logger } from '@fe/core';
import { CustomLayerInterface, CustomRenderMethodInput, Map as MaplibreMap } from 'maplibre-gl';
import fragmentSource from './symbol.fragment.glsl?raw';
import vertexSource from './symbol.vert.glsl?raw';

type ZoomExpression = number | ((zoom: number) => number);

export class SymbolLayer2 implements CustomLayerInterface {
    readonly type = 'custom';
    readonly renderingMode?: '2d';
    private shaderMap = new Map<string, WebGLProgram>();
    private gl: WebGL2RenderingContext | null = null;
    private aPos: GLint = 0; // TODO: use VAO
    private aCenter: GLint = 0; // TODO: use VAO
    private aIconColor: GLint = 0; // TODO: use VAO
    private vertexBuffer: WebGLBuffer | null = null;
    private centerBuffer: WebGLBuffer | null = null;
    private iconColorBuffer: WebGLBuffer | null = null;
    private texture: WebGLTexture | null = null;
    private colorRamp: WebGLTexture | null = null;
    private map: MaplibreMap | null = null;
    private instanceCount = 0;
    private _size: ZoomExpression = 24;
    private _opacity = 1.0;

    constructor(public readonly id: string) {}

    get opacity(): number {
        return this._opacity;
    }

    set opacity(value: number) {
        this._opacity = value;
        this.map?.triggerRepaint();
    }

    get size() {
        return this._size;
    }

    set size(value: ZoomExpression) {
        this._size = value;
        this.map?.triggerRepaint();
    }

    private getShader(gl: WebGLRenderingContext | WebGL2RenderingContext, shaderDescription: CustomRenderMethodInput['shaderData']) {
        // Pick a shader based on the current projection, defined by `variantName`.
        if (this.shaderMap.has(shaderDescription.variantName)) {
            return this.shaderMap.get(shaderDescription.variantName);
        }

        const vs = vertexSource
            .replace('__define__', shaderDescription.define)
            .replace('__prelude__', shaderDescription.vertexShaderPrelude);

        // create a vertex shader
        const vertexShader = gl.createShader(gl.VERTEX_SHADER)!;
        gl.shaderSource(vertexShader, vs);
        gl.compileShader(vertexShader);
        if (!gl.getShaderParameter(vertexShader, gl.COMPILE_STATUS)) {
            console.error(gl.getShaderInfoLog(vertexShader));
        }

        // create a fragment shader
        const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER)!;
        gl.shaderSource(fragmentShader, fragmentSource);
        gl.compileShader(fragmentShader);
        if (!gl.getShaderParameter(fragmentShader, gl.COMPILE_STATUS)) {
            console.error(gl.getShaderInfoLog(fragmentShader));
        }

        // link the two shaders into a WebGL program
        const program = gl.createProgram();
        gl.attachShader(program, vertexShader);
        gl.attachShader(program, fragmentShader);
        gl.linkProgram(program);

        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            console.error(gl.getProgramInfoLog(program));
        }

        this.aPos = gl.getAttribLocation(program, 'a_pos');
        this.aCenter = gl.getAttribLocation(program, 'a_center');
        this.aIconColor = gl.getAttribLocation(program, 'a_color');

        this.shaderMap.set(shaderDescription.variantName, program);

        return program;
    }

    setColorRamp(ramp: Uint8Array) {
        if (!this.gl) {
            return;
        }
        const gl = this.gl;
        gl.bindTexture(gl.TEXTURE_2D, this.colorRamp);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, ramp.length / 4, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, ramp);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        this.map?.triggerRepaint();
    }

    setImages(imageName: string[]) {
        if (!this.gl) {
            return;
        }

        const gl = this.gl;
        const images = imageName.map((x) => this.map!.getImage(x));
        if (images.some((x) => !x)) {
            logger.warn('some images not found');
            return;
        }

        const imgs = images.filter(Boolean);
        if (imgs.some((x) => x.data.width !== imgs[0].data.width || x.data.height !== imgs[0].data.height)) {
            logger.warn('All images must be the same size');
            return;
        }

        if (imgs.length === 0) {
            return;
        }

        const w = imgs[0].data.width;
        const h = imgs[0].data.height;

        const wh = w * h;
        const bitmap = new Uint8Array(wh * imgs.length * 4);
        for (let i = 0; i < imgs.length; i++) {
            const img = imgs[i].data.data;
            for (let j = 0; j < img.length; j++) {
                bitmap[wh * i * 4 + j] = img[j];
            }
        }

        gl.bindTexture(gl.TEXTURE_2D, this.texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h * imgs.length, 0, gl.RGBA, gl.UNSIGNED_BYTE, bitmap);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        this.map?.triggerRepaint();
    }

    setData(positions: Float32Array, colorIndexes: Uint8Array) {
        if (!this.gl) {
            return;
        }
        const gl = this.gl;
        gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, 1, 1, 1, -1, -1, 1, -1]), gl.STATIC_DRAW);
        this.instanceCount = positions.length / 2;

        gl.bindBuffer(gl.ARRAY_BUFFER, this.centerBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.iconColorBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, colorIndexes, gl.STATIC_DRAW);
        this.map?.triggerRepaint();
    }

    onAdd(map: MaplibreMap, gl: WebGL2RenderingContext): void {
        this.map = map;
        this.gl = gl;
        this.vertexBuffer = gl.createBuffer();
        this.centerBuffer = gl.createBuffer();
        this.iconColorBuffer = gl.createBuffer();
        this.texture = gl.createTexture();
        this.colorRamp = gl.createTexture();
    }

    onRemove(map: MaplibreMap, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
        this.map = null;
        gl.deleteBuffer(this.vertexBuffer);
        gl.deleteBuffer(this.centerBuffer);
        gl.deleteBuffer(this.iconColorBuffer);
        gl.deleteTexture(this.texture);
        gl.deleteTexture(this.colorRamp);

        gl.disableVertexAttribArray(this.aPos);
        gl.disableVertexAttribArray(this.aCenter);
        gl.disableVertexAttribArray(this.aIconColor);

        this.vertexBuffer = null;
        this.centerBuffer = null;
        this.texture = null;
        this.colorRamp = null;
        this.iconColorBuffer = null;

        for (const program of this.shaderMap.values()) {
            gl.deleteProgram(program);
        }

        this.shaderMap.clear();
        this.gl = null;
    }

    render(_gl: WebGLRenderingContext | WebGL2RenderingContext, args: CustomRenderMethodInput) {
        if (!this.map) {
            return;
        }
        const gl = _gl as WebGL2RenderingContext;
        const globe = args.shaderData.variantName === 'globe';
        (globe ? [0] : this.getWorldOffsets()).forEach((offset) => {
            const program = this.getShader(gl, args.shaderData)!;
            gl.enable(gl.BLEND);
            gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
            gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.SRC_ALPHA, gl.DST_ALPHA);

            gl.useProgram(program);
            gl.uniformMatrix4fv(
                gl.getUniformLocation(program, 'u_projection_fallback_matrix'),
                false,
                args.defaultProjectionData.fallbackMatrix // convert mat4 from gl-matrix to a plain array
            );
            gl.uniformMatrix4fv(
                gl.getUniformLocation(program, 'u_projection_matrix'),
                false,
                args.defaultProjectionData.mainMatrix // convert mat4 from gl-matrix to a plain array
            );
            gl.uniform4f(
                gl.getUniformLocation(program, 'u_projection_tile_mercator_coords'),
                ...args.defaultProjectionData.tileMercatorCoords
            );

            gl.uniform4f(gl.getUniformLocation(program, 'u_projection_clipping_plane'), ...args.defaultProjectionData.clippingPlane);
            gl.uniform1f(gl.getUniformLocation(program, 'u_projection_transition'), args.defaultProjectionData.projectionTransition);
            gl.uniform1f(gl.getUniformLocation(program, 'u_worldXOffset'), offset);
            gl.uniform1f(gl.getUniformLocation(program, 'u_opacity'), this.opacity);

            const zoom = this.map!.getZoom();
            const radius = (typeof this._size === 'function' ? this._size(zoom) : this._size) / 2;
            gl.uniform1f(gl.getUniformLocation(program, 'u_radius'), radius);
            gl.uniform2f(gl.getUniformLocation(program, 'units_per_pixel'), ...this.map!._camera.transform.pixelsToGLUnits);

            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, this.texture);
            gl.uniform1i(gl.getUniformLocation(program, 'u_texture'), 0);

            gl.activeTexture(gl.TEXTURE1);
            gl.bindTexture(gl.TEXTURE_2D, this.colorRamp);
            gl.uniform1i(gl.getUniformLocation(program, 'u_ramp'), 1);
            gl.uniform1f(gl.getUniformLocation(program, 'u_device_pixel_ratio'), this.map!._camera.transform.getPixelScale());

            gl.bindBuffer(gl.ARRAY_BUFFER, this.centerBuffer);
            gl.enableVertexAttribArray(this.aCenter);
            gl.vertexAttribPointer(this.aCenter, 2, gl.FLOAT, false, 0, 0);
            gl.vertexAttribDivisor(this.aCenter, 1);

            gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
            gl.enableVertexAttribArray(this.aPos);
            gl.vertexAttribPointer(this.aPos, 2, gl.FLOAT, false, 0, 0);
            gl.vertexAttribDivisor(this.aPos, 0);

            gl.bindBuffer(gl.ARRAY_BUFFER, this.iconColorBuffer);
            gl.enableVertexAttribArray(this.aIconColor);
            gl.vertexAttribIPointer(this.aIconColor, 1, gl.UNSIGNED_BYTE, 0, 0);
            gl.vertexAttribDivisor(this.aIconColor, 1);

            gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.instanceCount);
        });
        gl.vertexAttribDivisor(this.aCenter, 0);
        gl.vertexAttribDivisor(this.aPos, 0);
        gl.vertexAttribDivisor(this.aIconColor, 0);
    }

    private getWorldOffsets() {
        const bounds = this.map!.getBounds();
        const west = bounds.getWest();
        const east = bounds.getEast();

        const x1 = 0 - Math.ceil((Math.abs(west) - 180) / 360);
        const x2 = Math.ceil((Math.abs(east) - 180) / 360);

        const worldOffsets = [];
        for (let i = x1; i <= x2; i++) {
            worldOffsets.push(i);
        }
        return worldOffsets;
    }
}
